import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Tooltip,
    alpha,
    useTheme,
    Snackbar,
    Alert,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Autocomplete,
    Stack,
    FormHelperText,
} from '@mui/material';
import {
    Search as SearchIcon,
    CloudUpload as UploadIcon,
    ContentCopy as CopyIcon,
    LinkedIn as LinkedInIcon,
    Close as ClearIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import leadService, { Lead } from '../../../services/lead.service';

const LeadList: React.FC = () => {
    const theme = useTheme();

    // State variables
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [uploading, setUploading] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<string | string[] | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    // Upload modal state
    const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
    const [categories, setCategories] = useState<string[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
    const [categorySearchInput, setCategorySearchInput] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [formErrors, setFormErrors] = useState<{ category?: string; file?: string }>({});

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Fetch leads on mount and when page/rows/search changes
    useEffect(() => {
        fetchLeads();
    }, [page, rowsPerPage, searchQuery]);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const response = await leadService.getLeadList({
                startToken: page + 1,
                endToken: rowsPerPage,
                search: searchQuery,
            });
            setLeads(response.data);
            setTotalRows(response.record_count || response.data.length);
        } catch (error) {
            console.error('Error fetching leads:', error);
            showSnackbar('Failed to fetch lead list', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async (search: string) => {
        setCategoriesLoading(true);
        try {
            const response = await leadService.getLeadCategories({
                search,
                start: 0,
                limit: 100,
            });
            const names = (response.data || [])
                .map((c) => c?.name)
                .filter((n): n is string => typeof n === 'string' && n.length > 0);
            setCategories(names);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Debounced category search whenever the modal is open and the input changes.
    useEffect(() => {
        if (!uploadModalOpen) return;
        const timer = setTimeout(() => {
            fetchCategories(categorySearchInput.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [categorySearchInput, uploadModalOpen]);

    const handleOpenUploadModal = () => {
        setSelectedCategory('');
        setCategorySearchInput('');
        setUploadFile(null);
        setFormErrors({});
        setUploadModalOpen(true);
    };

    const handleCloseUploadModal = () => {
        if (uploading) return;
        setUploadModalOpen(false);
        setSelectedCategory('');
        setCategorySearchInput('');
        setUploadFile(null);
        setFormErrors({});
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleModalFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setFormErrors((prev) => ({ ...prev, file: 'Please select a CSV file' }));
            setUploadFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        setUploadFile(file);
        setFormErrors((prev) => ({ ...prev, file: undefined }));
    };

    const handleSubmitUpload = async () => {
        const trimmedCategory = selectedCategory.trim();
        const errors: { category?: string; file?: string } = {};
        if (!trimmedCategory) errors.category = 'Category is required';
        if (!uploadFile) errors.file = 'CSV file is required';
        if (errors.category || errors.file) {
            setFormErrors(errors);
            return;
        }

        const isNew = !categories.some(
            (c) => c.toLowerCase() === trimmedCategory.toLowerCase()
        );

        setUploading(true);
        try {
            await leadService.uploadLeads(uploadFile!, trimmedCategory, isNew);
            showSnackbar('Leads uploaded successfully', 'success');
            setUploadModalOpen(false);
            setSelectedCategory('');
            setCategorySearchInput('');
            setUploadFile(null);
            setFormErrors({});
            setPage(0);
            fetchLeads();
        } catch (error) {
            console.error('Upload error:', error);
            showSnackbar('Error uploading leads', 'error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setPage(0);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        setSelectedLeads([]);
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = leads.map((lead) => lead._id).filter((id): id is string => !!id);
            setSelectedLeads(newSelected);
        } else {
            setSelectedLeads([]);
        }
    };

    const handleSelectOne = (_event: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const selectedIndex = selectedLeads.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedLeads, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedLeads.slice(1));
        } else if (selectedIndex === selectedLeads.length - 1) {
            newSelected = newSelected.concat(selectedLeads.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedLeads.slice(0, selectedIndex),
                selectedLeads.slice(selectedIndex + 1)
            );
        }

        setSelectedLeads(newSelected);
    };

    const handleDeleteClick = (id: string) => {
        setItemToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const handleBulkDeleteClick = () => {
        setItemToDelete(selectedLeads);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;

        setDeleting(true);
        try {
            if (Array.isArray(itemToDelete)) {
                await leadService.deleteMultipleLeads(itemToDelete);
                showSnackbar(`${itemToDelete.length} leads deleted successfully`, 'success');
                setSelectedLeads([]);
            } else {
                await leadService.deleteLead(itemToDelete);
                showSnackbar('Lead deleted successfully', 'success');
                setSelectedLeads(selectedLeads.filter(id => id !== itemToDelete));
            }
            fetchLeads();
        } catch (error) {
            console.error('Delete error:', error);
            showSnackbar('Failed to delete leads', 'error');
        } finally {
            setDeleting(false);
            setDeleteConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        if (!text || text === 'N/A') return;
        navigator.clipboard.writeText(text).then(() => {
            showSnackbar(`${label} copied to clipboard`, 'success');
        }).catch(err => {
            console.error('Failed to copy: ', err);
            showSnackbar('Failed to copy text', 'error');
        });
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        position: 'relative',
                        display: 'inline-block',
                        '&:after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -8,
                            left: 0,
                            width: 40,
                            height: 4,
                            backgroundColor: theme.palette.secondary.main,
                            borderRadius: 2,
                        }
                    }}
                >
                    Lead List
                </Typography>

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<UploadIcon />}
                        onClick={handleOpenUploadModal}
                        sx={{
                            px: 3,
                            boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                    >
                        Upload CSV
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Paper
                elevation={2}
                sx={{
                    p: 2,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <TextField
                    label="Search Leads"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search by name, email, company..."
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={handleClearSearch}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 500 }}
                />
                <Button
                    variant="contained"
                    onClick={fetchLeads}
                    sx={{ ml: 1 }}
                >
                    Search
                </Button>
                {selectedLeads.length > 0 && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleBulkDeleteClick}
                        sx={{ ml: 'auto' }}
                    >
                        Delete Selected ({selectedLeads.length})
                    </Button>
                )}
            </Paper>

            {/* Table */}
            <Paper elevation={3}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                                        checked={leads.length > 0 && selectedLeads.length === leads.length}
                                        onChange={handleSelectAll}
                                        inputProps={{ 'aria-label': 'select all leads' }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Level</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>LinkedIn</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={40} />
                                    </TableCell>
                                </TableRow>
                            ) : leads.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                        <Typography color="textSecondary">No leads found. Upload a CSV to get started.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                leads.map((lead, index) => (
                                    <TableRow
                                        key={lead._id || index}
                                        hover
                                        selected={lead._id ? selectedLeads.indexOf(lead._id) !== -1 : false}
                                    >
                                        <TableCell padding="checkbox">
                                            {lead._id && (
                                                <Checkbox
                                                    checked={selectedLeads.indexOf(lead._id) !== -1}
                                                    onChange={(event) => handleSelectOne(event, lead._id!)}
                                                    inputProps={{ 'aria-label': 'select lead' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(page * rowsPerPage) + index + 1}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {lead.email_address || 'N/A'}
                                                {lead.email_address && lead.email_address !== 'N/A' && (
                                                    <IconButton size="small" onClick={() => copyToClipboard(lead.email_address, 'Email')}>
                                                        <CopyIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{lead.categoryId?.name || 'N/A'}</TableCell>
                                        <TableCell>{lead.management_level || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {lead.mobile_phone || lead.direct_phone_number || lead.company_hq_phone || 'N/A'}
                                                {(lead.mobile_phone || lead.direct_phone_number || lead.company_hq_phone) && (
                                                    <IconButton size="small" onClick={() => copyToClipboard(lead.mobile_phone || lead.direct_phone_number || lead.company_hq_phone, 'Phone number')}>
                                                        <CopyIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            {lead.linkedin_url ? (
                                                <Tooltip title="View LinkedIn Profile">
                                                    <IconButton
                                                        component="a"
                                                        href={lead.linkedin_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ color: '#0a66c2' }}
                                                    >
                                                        <LinkedInIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {lead._id && (
                                                <Tooltip title="Delete Lead">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(lead._id!)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Snackbar for notifications */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => !deleting && setDeleteConfirmOpen(false)}
            >
                <DialogTitle>
                    {Array.isArray(itemToDelete) ? 'Delete Multiple Leads' : 'Delete Lead'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {Array.isArray(itemToDelete) ? `these ${itemToDelete.length} leads` : 'this lead'}?
                        This action reflects a permanent change in the active list.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting && <CircularProgress size={20} color="inherit" />}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload CSV Modal */}
            <Dialog
                open={uploadModalOpen}
                onClose={handleCloseUploadModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Upload CSV</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Autocomplete
                            freeSolo
                            selectOnFocus
                            handleHomeEndKeys
                            options={categories}
                            loading={categoriesLoading}
                            value={selectedCategory || null}
                            inputValue={categorySearchInput}
                            onChange={(_event, newValue) => {
                                let next = '';
                                if (typeof newValue === 'string') {
                                    next = newValue.startsWith('__create__::')
                                        ? newValue.slice('__create__::'.length)
                                        : newValue;
                                }
                                setSelectedCategory(next);
                                setCategorySearchInput(next);
                                if (next) setFormErrors((prev) => ({ ...prev, category: undefined }));
                            }}
                            onInputChange={(_event, newInputValue, reason) => {
                                if (reason === 'reset') return;
                                setCategorySearchInput(newInputValue);
                                setSelectedCategory(newInputValue);
                                if (newInputValue) {
                                    setFormErrors((prev) => ({ ...prev, category: undefined }));
                                }
                            }}
                            filterOptions={(options, params) => {
                                // Server already filtered by `search`; just append the Create row
                                // when the typed input doesn't exactly match an existing option.
                                const input = params.inputValue.trim();
                                const exists = options.some(
                                    (opt) => opt.toLowerCase() === input.toLowerCase()
                                );
                                return input && !exists
                                    ? [...options, `__create__::${input}`]
                                    : options;
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option !== 'string') return '';
                                return option.startsWith('__create__::')
                                    ? option.slice('__create__::'.length)
                                    : option;
                            }}
                            renderOption={(props, option) => {
                                if (option.startsWith('__create__::')) {
                                    const name = option.slice('__create__::'.length);
                                    return (
                                        <li {...props} key={`create-${name}`}>
                                            <AddIcon sx={{ mr: 1, fontSize: 18, color: theme.palette.primary.main }} />
                                            <Typography component="span">
                                                Create&nbsp;<strong>"{name}"</strong>
                                            </Typography>
                                        </li>
                                    );
                                }
                                return (
                                    <li {...props} key={option}>
                                        {option}
                                    </li>
                                );
                            }}
                            disabled={uploading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category *"
                                    placeholder="Select or type to create a category"
                                    error={!!formErrors.category}
                                    helperText={
                                        formErrors.category ||
                                        'Pick from the list or type a new name and press Enter to create.'
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {categoriesLoading ? (
                                                    <CircularProgress color="inherit" size={18} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Box>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleModalFileChange}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                            />
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={uploadFile ? <FileIcon /> : <UploadIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                color={formErrors.file ? 'error' : 'primary'}
                                sx={{
                                    py: 2,
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    borderStyle: 'dashed',
                                }}
                            >
                                {uploadFile ? uploadFile.name : 'Choose CSV File *'}
                            </Button>
                            <FormHelperText error={!!formErrors.file} sx={{ mx: 1.5 }}>
                                {formErrors.file || 'Only .csv files are accepted.'}
                            </FormHelperText>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseUploadModal} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitUpload}
                        variant="contained"
                        color="primary"
                        disabled={uploading || !selectedCategory.trim() || !uploadFile}
                        startIcon={
                            uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />
                        }
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeadList;